import { create } from "zustand";

import type { BuilderMessage } from "@/features/chat/builder/builder-types";

/**
 * In-memory live transcript buffer.
 * Durable SoT is build-session `uiMessages` (Memory text is fallback); this only
 * holds tokens for the active (or just-switched-away) SSE so switching chats
 * mid-stream does not drop them.
 */
type ChatLiveState = {
  buffers: Record<string, BuilderMessage[]>;
  setBuffer: (sessionId: string, messages: BuilderMessage[]) => void;
  clearBuffer: (sessionId: string) => void;
  clearAll: () => void;
};

export const useChatLiveStore = create<ChatLiveState>((set) => ({
  buffers: {},
  setBuffer: (sessionId, messages) => {
    const id = sessionId.trim();
    if (!id) return;
    set((state) => ({ buffers: { ...state.buffers, [id]: messages } }));
  },
  clearBuffer: (sessionId) => {
    const id = sessionId.trim();
    if (!id) return;
    set((state) => {
      if (!(id in state.buffers)) return state;
      const next = { ...state.buffers };
      delete next[id];
      return { buffers: next };
    });
  },
  clearAll: () => set({ buffers: {} }),
}));

export function getLiveBuffer(sessionId: string): BuilderMessage[] {
  const id = sessionId.trim();
  if (!id) return [];
  return useChatLiveStore.getState().buffers[id] ?? [];
}

export function setLiveBuffer(sessionId: string, messages: BuilderMessage[]) {
  useChatLiveStore.getState().setBuffer(sessionId, messages);
}

export function clearLiveBuffer(sessionId: string) {
  useChatLiveStore.getState().clearBuffer(sessionId);
}

export function resetLiveStoreForTests() {
  useChatLiveStore.getState().clearAll();
}
